import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostListener,
  Input,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

/**
 * Checkbox. Custom visuals because the design-system spec doesn't
 * formalize them — derived from form-input + brand tokens.
 *
 *   <bv-checkbox [(ngModel)]="agreed">I agree to the terms</bv-checkbox>
 */
@Component({
    selector: "bv-checkbox",
    templateUrl: "./checkbox.component.html",
    styleUrls: ["./checkbox.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BvCheckboxComponent),
            multi: true,
        },
    ],
    host: {
        class: "bv-checkbox",
        role: "checkbox",
        "[attr.aria-checked]": "checked",
        "[attr.aria-disabled]": "disabled || null",
        "[attr.tabindex]": "disabled ? -1 : 0",
        "[class.bv-checkbox--checked]": "checked",
        "[class.bv-checkbox--disabled]": "disabled",
    }
})
export class BvCheckboxComponent implements ControlValueAccessor {
  @Input() checked = false;
  @Input() disabled = false;

  private onChange: (v: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  @HostListener("click", ["$event"])
  onClick(event: Event): void {
    this.toggle(event);
  }

  @HostListener("keydown.space", ["$event"])
  onKeydown(event: Event): void {
    this.toggle(event);
  }

  private toggle(event: Event): void {
    if (this.disabled) return;
    event.preventDefault();
    this.checked = !this.checked;
    this.onChange(this.checked);
    this.onTouched();
    this.cdr.markForCheck();
  }

  writeValue(value: unknown): void {
    this.checked = !!value;
    this.cdr.markForCheck();
  }
  registerOnChange(fn: (v: boolean) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }
}
